// ============================================================================
// PARTICLE SYSTEM
// ============================================================================

import CONFIG from '../config.js';

class Particle {
    constructor(x, y, type, velocity, wind = null) {
        this.x = x;
        this.y = y;
        this.type = type;
        this.vx = velocity.x;
        this.vy = velocity.y;
        this.wind = wind; // Wind direction for leaves
        this.lifetime = type === 'wood' ? 0.5 + Math.random() * 0.5 : 
                       type === 'leaf' ? 8 + Math.random() * 2 : // Leaves live 8-10 seconds
                       0.8 + Math.random() * 0.7;
        this.maxLifetime = this.lifetime;
        this.size = type === 'wood' ? 2 + Math.random() * 3 : 
                   type === 'leaf' ? 3 + Math.random() * 4 :
                   10 + Math.random() * 10;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 5;
        this.initialSize = this.size;
        
        // For leaves, store a random color (light or dark green)
        if (type === 'leaf') {
            this.leafColor = Math.random() > 0.5 ? CONFIG.colors.leafParticleLight : CONFIG.colors.leafParticleDark;
        }
    }

    update(dt, wind = null) {
        this.x += this.vx * dt;
        this.y += this.vy * dt;

        if (this.type === 'wood') {
            this.vy += 200 * dt;
        }

        if (this.type === 'leaf') {
            // No wind or gravity - leaves just drift outward and settle
            // Add slight random turbulence for natural movement
            const turbulenceX = (Math.random() - 0.5) * 20 * dt;
            const turbulenceY = (Math.random() - 0.5) * 20 * dt;
            this.vx += turbulenceX;
            this.vy += turbulenceY;
            
            // Gentle rotation with some randomness
            this.rotationSpeed += (Math.random() - 0.5) * 1 * dt;
            this.rotationSpeed *= 0.99;
        }

        if (this.type === 'dust') {
            this.size = this.initialSize * (1 + (1 - this.lifetime / this.maxLifetime) * 0.5);
        }

        this.rotation += this.rotationSpeed * dt;
        this.lifetime -= dt;

        // Friction (leaves slow down gradually and settle on floor)
        const friction = this.type === 'leaf' ? 0.992 : 0.98; // Leaves slow down more gradually
        this.vx *= friction;
        this.vy *= friction;
        
        // When leaves are moving very slowly, they've "settled" on the floor
        // Keep them alive but barely moving
        if (this.type === 'leaf') {
            const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
            if (speed < 5) {
                // Almost stopped - reduce movement even more
                this.vx *= 0.95;
                this.vy *= 0.95;
            }
        }
    }

    draw(ctx, camera) {
        const alpha = this.lifetime / this.maxLifetime;

        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        ctx.globalAlpha = alpha;

        if (this.type === 'wood') {
            ctx.fillStyle = CONFIG.colors.woodParticle;
            ctx.fillRect(-this.size / 2, -this.size / 2, this.size, this.size);
        } else if (this.type === 'leaf') {
            // Draw leaf as a simple oval/leaf shape with random green color
            ctx.fillStyle = this.leafColor || CONFIG.colors.leafParticle || '#228B22';
            ctx.beginPath();
            ctx.ellipse(0, 0, this.size, this.size * 0.6, this.rotation, 0, Math.PI * 2);
            ctx.fill();
            // Add a stem (darker green)
            ctx.strokeStyle = '#006400';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(0, -this.size * 0.6);
            ctx.lineTo(0, -this.size);
            ctx.stroke();
        } else {
            ctx.fillStyle = CONFIG.colors.dustParticle;
            ctx.beginPath();
            ctx.arc(0, 0, this.size, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();
    }

    isDead() {
        return this.lifetime <= 0;
    }
}

export class ParticleSystem {
    constructor() {
        this.particles = [];
    }

    emit(x, y, count, aggression) {
        const woodCount = Math.floor(count * 0.7);
        for (let i = 0; i < woodCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 100 + Math.random() * 200 * aggression;
            this.particles.push(new Particle(x, y, 'wood', {
                x: Math.cos(angle) * speed,
                y: Math.sin(angle) * speed
            }));
        }

        const dustCount = Math.floor(count * 0.3);
        for (let i = 0; i < dustCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 50 + Math.random() * 100 * aggression;
            this.particles.push(new Particle(x, y, 'dust', {
                x: Math.cos(angle) * speed,
                y: Math.sin(angle) * speed
            }));
        }
    }

    emitLeaves(x, y, count, wind) {
        for (let i = 0; i < count; i++) {
            // Initial velocity from shaking - leaves fall outward in random directions
            const angle = Math.random() * Math.PI * 2; // Random direction
            const speed = 30 + Math.random() * 60; // Varied speeds
            
            this.particles.push(new Particle(x, y, 'leaf', {
                x: Math.cos(angle) * speed,
                y: Math.sin(angle) * speed
            }, null)); // No wind needed
        }
    }

    update(dt, wind = null) {
        // Wind parameter kept for compatibility but not used for leaves anymore
        for (let i = this.particles.length - 1; i >= 0; i--) {
            this.particles[i].update(dt, null); // No wind for leaves
            if (this.particles[i].isDead()) {
                this.particles.splice(i, 1);
            }
        }
    }

    draw(ctx, camera) {
        this.particles.forEach(p => p.draw(ctx, camera));
    }
}
